import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

// ============================================================
// SceneRenderer
// 将数据库中的 scene_3d JSON（elements 数组）渲染为 3D 场景对象。
// 每个 element 格式：
// {
//   "id": "hedgehog_ball",
//   "type": "character" | "prop" | "background",
//   "model": "hedgehog_ball",
//   "position": [x, y, z],
//   "scale": 1.0,
//   "animation": "bounce" | "sway" | "float_bob" | ...
// }
// ------------------------------------------------------------

export interface SceneElement {
  id: string
  type?: string
  model?: string
  position?: [number, number, number]
  scale?: number
  animation?: string
  color?: string
}

interface SceneRendererProps {
  elements: SceneElement[]
}

// ---- 模型 -> 基础几何体 + 颜色的启发式映射 ----
interface ShapeSpec {
  kind: 'sphere' | 'box' | 'cone' | 'cylinder' | 'torus' | 'icosahedron' | 'plane'
  color: string
  scale?: [number, number, number]
  emissive?: string
  emissiveIntensity?: number
}

function inferShape(model: string): ShapeSpec {
  const m = (model || '').toLowerCase()
  const pick = (arr: string[]): boolean => arr.some((k) => m.includes(k))

  if (pick(['sun', 'star'])) {
    return { kind: 'icosahedron', color: '#FFD54F', emissive: '#FFB300', emissiveIntensity: 0.8 }
  }
  if (pick(['moon', 'crescent'])) {
    return { kind: 'sphere', color: '#FFF9C4', emissive: '#FFECB3', emissiveIntensity: 0.6 }
  }
  if (pick(['cloud'])) {
    return { kind: 'sphere', color: '#FFFFFF', scale: [1.2, 0.7, 0.7] }
  }
  if (pick(['tree', 'oak', 'pine'])) {
    return { kind: 'cone', color: '#43A047', scale: [1, 1.6, 1] }
  }
  if (pick(['trunk', 'log'])) {
    return { kind: 'cylinder', color: '#8D6E63' }
  }
  if (pick(['mushroom'])) {
    return { kind: 'sphere', color: '#E53935', scale: [1, 0.6, 1] }
  }
  if (pick(['flower'])) {
    return { kind: 'sphere', color: '#FF8A65', scale: [0.4, 0.4, 0.4] }
  }
  if (pick(['grass', 'ground', 'hill', 'meadow', 'field', 'earth'])) {
    return { kind: 'plane', color: '#66BB6A', scale: [1, 1, 1] }
  }
  if (pick(['squirrel', 'hedgehog', 'bunny', 'rabbit', 'fox', 'bird', 'butterfly', 'child', 'girl', 'boy'])) {
    return { kind: 'sphere', color: '#FFB74D', scale: [0.8, 0.6, 0.6] }
  }
  if (pick(['rainbow'])) {
    return { kind: 'torus', color: '#FF6EC7' }
  }
  if (pick(['rock', 'stone', 'bush', 'leaf', 'acorn', 'pinecone', 'moss'])) {
    return { kind: 'sphere', color: '#8D6E63', scale: [0.6, 0.5, 0.6] }
  }
  if (pick(['heart'])) {
    return { kind: 'icosahedron', color: '#FF5252' }
  }
  return { kind: 'sphere', color: '#90A4AE', scale: [0.5, 0.5, 0.5] }
}

// ---- 动画实现 ----
type AnimFn = (t: number, elapsed: number) => { pos?: [number, number, number]; rot?: [number, number, number]; scale?: number }

const ANIMATIONS: Record<string, AnimFn> = {
  bounce: (t, e) => ({ pos: [0, Math.max(0, Math.sin(e * 3 + t)) * 0.3, 0] }),
  sway: (t, e) => ({ rot: [0, 0, Math.sin(e * 1.5 + t) * 0.2] }),
  float_bob: (t, e) => ({ pos: [0, Math.sin(e * 1.2 + t) * 0.25, 0] }),
  glow: (t, e) => ({ scale: 1 + Math.sin(e * 2 + t) * 0.08 }),
  glow_bright: (t, e) => ({ scale: 1 + Math.sin(e * 3 + t) * 0.15 }),
  glow_cycle: (t, e) => ({ scale: 1 + Math.sin(e * 1.6 + t) * 0.1 }),
  glow_soft: (t, e) => ({ scale: 1 + Math.sin(e * 1.2 + t) * 0.06 }),
  idle_breathe: (t, e) => ({ scale: 1 + Math.sin(e * 1 + t) * 0.05 }),
  idle_sway: (t, e) => ({ rot: [0, Math.sin(e * 0.8 + t) * 0.15, 0] }),
  shake: (t, e) => ({ pos: [Math.sin(e * 20 + t * 7) * 0.05, 0, 0] }),
  shiver: (t, e) => ({ pos: [Math.sin(e * 25 + t * 9) * 0.03, 0, 0], scale: 1 + Math.sin(e * 25) * 0.02 }),
  uncurl: (_t, e) => ({ scale: Math.min(1, e * 0.5) }),
  hop: (t, e) => ({ pos: [0, Math.abs(Math.sin(e * 4 + t)) * 0.4, 0] }),
  jump: (t, e) => ({ pos: [0, Math.abs(Math.sin(e * 2.5 + t)) * 0.6, 0] }),
  wave: (t, e) => ({ rot: [0, 0, Math.sin(e * 3 + t) * 0.3] }),
  nod: (t, e) => ({ rot: [Math.sin(e * 2 + t) * 0.2, 0, 0] }),
  look_up: (t, e) => ({ rot: [Math.sin(e * 1.5 + t) * 0.3, 0, 0] }),
  listen: (t, e) => ({ pos: [0, Math.sin(e * 2 + t) * 0.05, 0], rot: [0, Math.sin(e * 0.5) * 0.1, 0] }),
  cry: (t, e) => ({ pos: [0, Math.sin(e * 2 + t) * 0.08, 0], scale: 1 + Math.sin(e * 2) * 0.03 }),
  laugh: (t, e) => ({ rot: [0, Math.sin(e * 6 + t * 2) * 0.15, 0], scale: 1 + Math.sin(e * 6) * 0.06 }),
  clap: (t, e) => ({ rot: [0, Math.sin(e * 8 + t * 3) * 0.2, 0] }),
  cheer: (t, e) => ({ pos: [0, Math.abs(Math.sin(e * 5 + t)) * 0.35, 0] }),
  cuddle: (_t, e) => ({ scale: 1 + Math.sin(e * 1.5 + _t) * 0.04 }),
  color_cycle: (_t, e) => ({ rot: [0, e * 0.5, 0] }),
  blink: (_t, e) => ({ scale: Math.sin(e * 1 + _t) > 0.9 ? 0.85 : 1 }),
  float_soft: (_t, e) => ({ pos: [Math.sin(e * 0.8 + _t) * 0.2, Math.sin(e * 0.6 + _t) * 0.2, 0] }),
  stretch: (_t, e) => ({ scale: 1 + Math.sin(e * 1.2 + _t) * 0.1 }),
  yawn: (_t, e) => ({ scale: 1 + Math.max(0, Math.sin(e * 1.5 + _t)) * 0.15 }),
  fly_slow: (_t, e) => ({ pos: [Math.sin(e * 0.5 + _t) * 0.4, Math.sin(e * 0.8 + _t * 0.5) * 0.2, 0] }),
  fly_hover: (_t, e) => ({ pos: [Math.sin(e * 0.6 + _t) * 0.3, Math.sin(e * 1.4 + _t) * 0.15, 0] }),
  flutter: (_t, e) => ({ rot: [0, Math.sin(e * 6 + _t * 2) * 0.4, 0] }),
  spin: (_t, e) => ({ rot: [0, e * 2, 0] }),
  twinkle: (_t, e) => ({ scale: 1 + Math.max(0, Math.sin(e * 2 + _t)) * 0.3 }),
  blink_slow: (_t, e) => ({ scale: Math.sin(e * 0.8 + _t) > 0.85 ? 0.9 : 1 }),
  dodge: (_t, e) => ({ pos: [Math.sin(e * 3 + _t * 2) * 0.2, 0, 0] }),
  peek_out: (_t, e) => ({ pos: [0, -0.2 + Math.max(0, Math.sin(e * 2 + _t)) * 0.3, 0] }),
  sleep: (_t, e) => ({ scale: 1 + Math.max(0, Math.sin(e * 1 + _t)) * 0.08 }),
  dream: (_t, e) => ({ pos: [0, Math.sin(e * 0.9 + _t) * 0.2, 0], scale: 1 + Math.sin(e * 0.9) * 0.05 }),
  grow: (_t, e) => ({ scale: Math.min(1.4, 0.4 + e * 0.15) }),
  breathe_glow: (_t, e) => ({ scale: 1 + Math.sin(e * 1.8 + _t) * 0.12 }),
  slow_sway: (_t, e) => ({ rot: [0, 0, Math.sin(e * 0.6 + _t) * 0.12] }),
  shimmer: (_t, e) => ({ scale: 1 + Math.sin(e * 2.4 + _t) * 0.1, rot: [0, e * 0.3, 0] }),
}

function SceneElementMesh({ element, seed }: { element: SceneElement; seed: number }) {
  const meshRef = useRef<THREE.Mesh>(null)
  const spec = useMemo(() => inferShape(element.model || element.id), [element.model, element.id])
  const pos = element.position || [0, 0, 0]
  const scale = element.scale || 1
  const animKey = element.animation || 'idle_breathe'
  const anim = ANIMATIONS[animKey] || ANIMATIONS.idle_breathe

  useFrame((state) => {
    if (!meshRef.current) return
    const elapsed = state.clock.elapsedTime
    const out = anim(seed, elapsed)
    if (out.pos) {
      meshRef.current.position.set(
        pos[0] + out.pos[0],
        pos[1] + out.pos[1],
        pos[2] + out.pos[2]
      )
    } else {
      meshRef.current.position.set(pos[0], pos[1], pos[2])
    }
    if (out.rot) {
      meshRef.current.rotation.set(out.rot[0], out.rot[1], out.rot[2])
    }
    if (out.scale) {
      const s = (spec.scale ? Math.max(...spec.scale) : 1) * scale * out.scale
      meshRef.current.scale.setScalar(s)
    } else {
      const s = (spec.scale ? Math.max(...spec.scale) : 1) * scale
      meshRef.current.scale.setScalar(s)
    }
  })

  const material = (
    <meshStandardMaterial
      color={spec.color}
      emissive={spec.emissive || '#000000'}
      emissiveIntensity={spec.emissiveIntensity || 0}
      roughness={0.6}
      metalness={0.1}
    />
  )

  return (
    <mesh ref={meshRef} position={pos} castShadow receiveShadow>
      {spec.kind === 'sphere' && <sphereGeometry args={[0.5, 20, 20]} />}
      {spec.kind === 'box' && <boxGeometry args={[0.8, 0.8, 0.8]} />}
      {spec.kind === 'cone' && <coneGeometry args={[0.6, 1.4, 12]} />}
      {spec.kind === 'cylinder' && <cylinderGeometry args={[0.3, 0.35, 1.2, 12]} />}
      {spec.kind === 'torus' && <torusGeometry args={[0.8, 0.15, 10, 24]} />}
      {spec.kind === 'icosahedron' && <icosahedronGeometry args={[0.5, 0]} />}
      {spec.kind === 'plane' && (
        <planeGeometry args={[6, 3, 1, 1]} />
      )}
      {material}
    </mesh>
  )
}

export default function SceneRenderer({ elements }: SceneRendererProps) {
  const seeds = useMemo(() => elements.map(() => Math.random() * 100), [elements])

  return (
    <group>
      {elements.map((el, i) => (
        <SceneElementMesh key={el.id + i} element={el} seed={seeds[i]} />
      ))}
    </group>
  )
}